import React, {useId} from 'react'
import styled from 'styled-components'
import {InfoHint} from './InfoHint'
import {isBlankNode, StatValue, statValueState} from './internal/StatValue'

/**
 * KeyValueList
 *
 * Label-and-figure rows: the label on the left, the figure right-aligned in navy, tabular
 * numbers (Avg call time, Assist ratio, Split orders, a money card). A `<dl>`, one row per item.
 *
 * - `size="md"` (default) is a compact caption-size list; `size="lg"` sets the figures at
 *   subtitle size in bold, for a card whose figures are the point.
 * - `caption` puts an uppercase mini label above the rows, with an optional InfoHint
 *   (`${testId}-info`), and names the list for assistive tech.
 * - `dividerAfter` draws a hairline under the named rows (never under the last one).
 * - Each row has its own state, by the shared precedence loading > error > pending > empty >
 *   value (see StatValue). A failed row reads 'Couldn’t load', never '—'. The list's own
 *   `loading` / `error` apply to every row.
 * - A row's `hint` is an InfoHint beside its label (`${rowTestId}-info`). A row reading '—'
 *   with an `emptyReason`, or its pending text with a `pendingReason` ('Reconnect Zoom'),
 *   explains itself through that same trigger, the reason added to the hint; a row with no hint
 *   makes the '—' or the pending text itself the trigger. One trigger per row either way, and a
 *   reason shows only in its own state.
 *
 * Test ids: the list's `data-testid` is on the root; each row's is on its figure (`<dd>`),
 * `${testId}-${id}` unless the item names its own.
 *
 * @example
 *   <KeyValueList caption="Avg call time" captionHint={definition}
 *     data-testid="staffer-metrics-avg-call-time"
 *     items={[
 *       {id: 'job-orders', label: 'Job orders', pending: 'Reconnect Zoom', pendingReason: expired},
 *       {id: 'data-cleanup', label: 'Data clean-up', value: null, emptyReason: branchOnly},
 *     ]} />
 *   <KeyValueList size="lg" dividerAfter={['gross-wages']}
 *     items={[{id: 'total-billed', label: 'Total billed', value: '$303,452.00', mono: true}, …]} />
 */

export type KeyValueListSize = 'md' | 'lg'

export interface KeyValueItem {
  id: string
  label: string
  /** The figure. null, undefined, '' and false read '—'; 0 is a figure. */
  value?: React.ReactNode
  /** The row's InfoHint content, beside the label. */
  hint?: React.ReactNode
  /** The hint's accessible name (default `About ${label}`). */
  hintLabel?: string
  /** The figure in the mono face (money, ids). The state texts stay in the sans face. */
  mono?: boolean
  loading?: boolean
  /** Truthy = the figure could not be read: 'Couldn’t load'. */
  error?: Error | null | boolean
  /** The error text (default "Couldn’t load"). */
  errorLabel?: string
  /** Text in place of the figure, e.g. 'Not tracked yet'. */
  pending?: React.ReactNode
  /** Why the figure is '—'. */
  emptyReason?: React.ReactNode
  /** Why the figure reads its pending text, e.g. why 'Reconnect Zoom'. */
  pendingReason?: React.ReactNode
  /** On the figure. Default `${testId}-${id}`. */
  'data-testid'?: string
}

export interface KeyValueListProps {
  items: KeyValueItem[]
  size?: KeyValueListSize
  /** Ids of the rows a hairline follows. */
  dividerAfter?: string[]
  /** An uppercase mini label above the rows, e.g. 'Avg call time'. */
  caption?: string
  /** The caption's InfoHint content. */
  captionHint?: React.ReactNode
  /** The caption hint's accessible name (default `About ${caption}`). */
  captionHintLabel?: string
  /** Every row is loading. */
  loading?: boolean
  /** Every row failed. */
  error?: Error | null | boolean
  /** The error text for every row (default "Couldn’t load"); a row's own wins. */
  errorLabel?: string
  /** Names the list when there is no caption. */
  'aria-label'?: string
  'data-testid'?: string
}

interface SizeStyle {
  text: string
  figure: string
  weight: string
  pad: string
}

const SIZE: Record<KeyValueListSize, SizeStyle> = {
  md: {
    text: 'var(--fs-caption)',
    figure: 'inherit',
    weight: 'var(--fw-semibold)',
    pad: 'var(--space-1)',
  },
  lg: {
    text: 'var(--fs-body)',
    figure: 'var(--fs-subtitle)',
    weight: 'var(--fw-bold)',
    pad: 'calc(var(--space-1) * 1.5)',
  },
}

const Root = styled.div`
  font-family: var(--font-sans);
`

const Caption = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin-bottom: var(--space-2);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  letter-spacing: var(--ls-wide);
  text-transform: uppercase;
  color: var(--text-secondary);
`

const List = styled.dl<{$size: KeyValueListSize}>`
  margin: 0;
  font-size: ${({$size}) => SIZE[$size].text};
  line-height: var(--lh-normal);
  color: var(--text-primary);
`

const Row = styled.div<{$size: KeyValueListSize; $divider: boolean}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: ${({$size}) => SIZE[$size].pad} 0;

  ${({$size, $divider}) =>
    $divider &&
    `
    padding-bottom: calc(${SIZE[$size].pad} + var(--space-3));
    margin-bottom: var(--space-3);
    border-bottom: var(--border-thin) solid var(--border-subtle);
  `}
`

const Term = styled.dt`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
  margin: 0;
`

const Figure = styled.dd<{$size: KeyValueListSize; $mono: boolean}>`
  margin: 0;
  text-align: right;
  font-family: ${({$mono}) => ($mono ? 'var(--font-mono)' : 'inherit')};
  font-size: ${({$size}) => SIZE[$size].figure};
  font-weight: ${({$size}) => SIZE[$size].weight};
  font-variant-numeric: tabular-nums;
  color: var(--primary-800);
`

export function KeyValueList({
  items,
  size = 'md',
  dividerAfter = [],
  caption,
  captionHint,
  captionHintLabel,
  loading = false,
  error,
  errorLabel,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: KeyValueListProps) {
  const captionId = useId()
  const dividers = new Set(dividerAfter)
  const rows = items.map((item) => {
    const rowLoading = loading || Boolean(item.loading)
    const rowError = Boolean(error) || Boolean(item.error)
    return {
      item,
      rowLoading,
      rowError,
      state: statValueState({
        value: item.value,
        loading: rowLoading,
        error: rowError,
        pending: item.pending,
      }),
    }
  })
  const busy = rows.some((row) => row.state === 'loading')

  return (
    <Root
      data-testid={testId}
      role={caption ? 'group' : undefined}
      aria-labelledby={caption ? captionId : undefined}>
      {caption ? (
        <Caption>
          <span id={captionId}>{caption}</span>
          {isBlankNode(captionHint) ? null : (
            <InfoHint
              label={captionHintLabel ?? `About ${caption}`}
              content={captionHint}
              data-testid={testId ? `${testId}-info` : undefined}
            />
          )}
        </Caption>
      ) : null}
      <List $size={size} aria-label={caption ? undefined : ariaLabel} aria-busy={busy || undefined}>
        {rows.map(({item, rowLoading, rowError, state}, index) => {
          const rowTestId = item['data-testid'] ?? (testId ? `${testId}-${item.id}` : undefined)
          const infoTestId = rowTestId ? `${rowTestId}-info` : undefined
          const hintLabel = item.hintLabel ?? `About ${item.label}`
          const hasHint = !isBlankNode(item.hint)
          const stateReason =
            state === 'empty' ? item.emptyReason : state === 'pending' ? item.pendingReason : null
          const reason = isBlankNode(stateReason) ? null : stateReason
          // One trigger per row: a row with a hint carries its '—' or pending reason there.
          const hintContent =
            hasHint && reason !== null ? (
              <>
                {item.hint} {reason}
              </>
            ) : (
              item.hint
            )

          return (
            <Row
              key={item.id}
              $size={size}
              $divider={dividers.has(item.id) && index < items.length - 1}>
              <Term>
                {item.label}
                {hasHint ? (
                  <InfoHint label={hintLabel} content={hintContent} data-testid={infoTestId} />
                ) : null}
              </Term>
              <Figure $size={size} $mono={Boolean(item.mono)} data-testid={rowTestId}>
                <StatValue
                  value={item.value}
                  loading={rowLoading}
                  error={rowError}
                  errorLabel={item.errorLabel ?? errorLabel}
                  pending={item.pending}
                  emptyReason={hasHint ? undefined : item.emptyReason}
                  pendingReason={hasHint ? undefined : item.pendingReason}
                  reasonLabel={hintLabel}
                  reasonTestId={infoTestId}
                />
              </Figure>
            </Row>
          )
        })}
      </List>
    </Root>
  )
}
